import type { IRowNode } from 'ag-grid-community';
import type { AnyProperty } from '@/utilities.types';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import _ from 'lodash';

export const generateComparator = (column: string | undefined, conditionColumns: string[]) => {
  return <T extends AnyProperty>(valueA: T, valueB: T, nodeA: IRowNode<T>, nodeB: IRowNode<T>) => {
    if (column && conditionColumns.includes(column)) {
      try {
        let nodeAData = '';
        let nodeBData = '';
        if (nodeA?.data && nodeB?.data) {
          nodeAData = nodeA.data?.[column];
          nodeBData = nodeB.data?.[column];
        } else if (nodeA?.aggData && nodeB?.aggData) {
          nodeAData = nodeA.aggData?.[column];
          nodeBData = nodeB.aggData?.[column];
        }
        nodeAData = nodeAData.toString().toLowerCase();
        nodeBData = nodeBData.toString().toLowerCase();
        if (nodeAData === nodeBData) return 0;
        return nodeAData > nodeBData ? 1 : -1;
      } catch (err) {
        console.error(`Error in accessing ${column} in comparator:`, err);
      }
    } else {
      try {
        const toCompare = (x: T) => {
          if (!x) {
            return '';
          } else if (typeof x === 'string') {
            return (x as string).toString().toLocaleLowerCase();
          } else {
            return x;
          }
        };
        const valueAToCompare = toCompare(valueA);
        const valueBToCompare = toCompare(valueB);
        if (valueAToCompare === valueBToCompare) return 0;
        return valueAToCompare > valueBToCompare ? 1 : -1;
      } catch (err) {
        console.error(`Error in accessing ${valueA} or ${valueB} from ${column} in comparator:`, err);
      }
    }

    return 0;
  };
};

export const generateDateComparator = (column: string | number | undefined) => {
  return (valueA: number | string | Date | undefined, valueB: number | string | Date | undefined) => {
    try {
      let valueAToCompare = new Date(8640000000000000);
      let valueBToCompare = new Date(8640000000000000);
      if (valueA) {
        // number is always nanos
        valueAToCompare = _.isNumber(valueA)
          ? new Date(valueA / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND)
          : new Date(valueA);
      }
      if (valueB) {
        // number is always nanos
        valueBToCompare = _.isNumber(valueB)
          ? new Date(valueB / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND)
          : new Date(valueB);
      }
      if (valueAToCompare.getTime() === valueBToCompare.getTime()) return 0;
      return valueAToCompare > valueBToCompare ? 1 : -1;
    } catch (err) {
      console.error(`Error in accessing ${valueA} or ${valueB} from ${column} for group comparator:`, err);
    }

    return 0;
  };
};

export const generateDateFilterComparator = (column: string | undefined) => {
  return (filterValue: number | string | Date, cellValue: number | string | Date) => {
    if (!cellValue) return 1;
    try {
      let filterValueToCompare = new Date();
      let cellValueToCompare = new Date();
      if (filterValue) {
        filterValueToCompare = new Date(filterValue);
      }
      if (cellValue) {
        cellValueToCompare = new Date(cellValue);
      }
      if (filterValueToCompare === cellValueToCompare) return 0;
      return filterValueToCompare > cellValueToCompare ? -1 : 1;
    } catch (err) {
      console.error(`Error in accessing ${filterValue} or ${cellValue} from ${column} for group comparator:`, err);
    }
  };
};
